import React from 'react';
import 'ss-gizmo-font';
import styled from 'styled-components';
import formatCurrency from 'format-currency';

import env from 'bench-environment';
import DateFormatter from './DateFormatter';
import DataEntries, { getFromLedgers, type DataEntry } from './DataEntries';
import RowHighlighter from './RowHighlighter';
import { UsersIcon, FlagIcon } from './Icons';

const externalLinkIcon = '\uE202';

const Wrapper = styled.div<{ active: boolean }>`
  ${({ active }) => active && 'margin-bottom: 4px'};
`;

const RowWrapper = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 1fr 20px 100px;
  font-size: 12px;
  padding: 4px 0;
`;

const IconWrapper = styled.div`
  text-align: right;
`;

const Amount = styled.div`
  text-align: right;
`;

const TextCell = styled.p`
  margin: auto 0;
`;

type InternalNote = {
  body: string;
  flagged: boolean;
  timestamp: number;
  userFirstName: string;
  userLastName: string;
  userId: string;
};

export type JournalEntry = {
  id: string;
  documentDate: string;
  dataEntries: Array<DataEntry>;
  vendor: string;
  journalEntryId: string;
  internalNotes: Array<InternalNote>;
  amount: number;
};

type JournalEntriesProps = {
  data: Array<JournalEntry>;
  clientId: string;
  startDate: string;
  endDate: string;
  onSidebarToggle: (data: { journalEntryId?: string; ledgerId?: string }) => void;
};

const hasFlaggedInternalNotes = (notes: Array<InternalNote>) => notes.some(note => note.flagged);
const hasInternalNotes = (notes: Array<InternalNote>) => notes.length > 0;

const JournalEntries = ({
  data,
  clientId,
  startDate,
  endDate,
  onSidebarToggle,
}: JournalEntriesProps) => (
  <Wrapper active={data.length > 0}>
    {data.map(
      ({ id, documentDate, vendor, dataEntries, journalEntryId, internalNotes, amount }) => {
        const ledgerId = getFromLedgers(dataEntries)[0].ledgerAccountId;
        const basePath = `${env.location.bkTools}/${clientId}`;
        const datePath = `start/${startDate}/end/${endDate}`;

        const hasNotes = hasInternalNotes(internalNotes);
        const hasFlaggedNotes = hasFlaggedInternalNotes(internalNotes);

        const onRowClick = () => {
          if (onSidebarToggle) {
            onSidebarToggle({ journalEntryId });
          }
        };

        return (
          <RowHighlighter
            onRowClick={onRowClick}
            externalLink={`${basePath}/search/${ledgerId}/${datePath}?returl=income-statement&id=${journalEntryId}`}
            externalLinkIcon={externalLinkIcon}
            key={id}
          >
            <RowWrapper>
              <TextCell>
                <DateFormatter text={documentDate} inputFormat="MM/DD/YYYY" />
              </TextCell>
              <TextCell>{vendor}</TextCell>
              <DataEntries data={dataEntries} />
              <IconWrapper>
                {hasNotes && (hasFlaggedNotes ? <FlagIcon /> : <UsersIcon />)}
              </IconWrapper>
              <Amount>{formatCurrency(amount)}</Amount>
            </RowWrapper>
          </RowHighlighter>
        );
      },
    )}
  </Wrapper>
);

export default JournalEntries;
